import React, { useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";

const ROLE_META = {
  admin: { label: "Admin", cls: "border-rose-500 text-rose-600" },
  gerant: { label: "Gérant", cls: "border-primary text-primary" },
  etablissement: { label: "Établissement", cls: "border-sky-500 text-sky-600" },
  patient: { label: "Patient", cls: "border-purple-500 text-purple-600" },
  driver: { label: "Chauffeur", cls: "border-teal-500 text-teal-600" },
};

export default function AdminUsers() {
  const [items, setItems] = useState([]);
  const [role, setRole] = useState("all");
  const [q, setQ] = useState("");

  useEffect(() => {
    const params = role === "all" ? {} : { role };
    api.get("/admin/users", { params }).then((r) => setItems(r.data)).catch((e) => toast.error(apiError(e)));
  }, [role]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter((u) =>
      (u.email || "").toLowerCase().includes(s) || (u.name || "").toLowerCase().includes(s) || (u.company_name || "").toLowerCase().includes(s));
  }, [items, q]);

  return (
    <div className="space-y-5" data-testid="admin-users">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Utilisateurs</h1>
          <p className="text-muted-foreground text-sm">{items.length} compte(s) tous rôles confondus.</p>
        </div>
        <div className="flex items-end gap-2">
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger className="w-44" data-testid="admin-users-role-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les rôles</SelectItem>
              <SelectItem value="gerant">Gérants</SelectItem>
              <SelectItem value="etablissement">Établissements</SelectItem>
              <SelectItem value="patient">Patients</SelectItem>
              <SelectItem value="driver">Chauffeurs</SelectItem>
              <SelectItem value="admin">Admins</SelectItem>
            </SelectContent>
          </Select>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher…" className="w-64" data-testid="admin-users-search" />
        </div>
      </div>

      <Card className="p-0 rounded-md shadow-none overflow-hidden" data-testid="admin-users-table">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nom</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Rôle</TableHead>
              <TableHead>Société / Établissement</TableHead>
              <TableHead>Créé le</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">Aucun utilisateur.</TableCell></TableRow>
            )}
            {filtered.map((u) => {
              const m = ROLE_META[u.role] || { label: u.role, cls: "" };
              return (
                <TableRow key={u.id} data-testid={`admin-user-row-${u.id}`}>
                  <TableCell className="font-medium">{u.name || "—"}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{u.email}</TableCell>
                  <TableCell><Badge variant="outline" className={m.cls}>{m.label}</Badge></TableCell>
                  <TableCell className="text-sm">{u.company_name || "—"}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{u.created_at ? new Date(u.created_at).toLocaleDateString("fr-FR") : "—"}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
